import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '@/components/NavigationBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { GuestContactDialog } from '@/components/GuestContactDialog';
import { 
  guestContactService, 
  GuestContact, 
  CreateGuestContactData 
} from '@/services/guestContactService';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Download,
  Filter,
  X,
  Users,
  Mail,
  Phone,
  Tag,
  ChevronDown,
  MoreHorizontal
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

const GuestList = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [contacts, setContacts] = useState<GuestContact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<GuestContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  useEffect(() => {
    // Don't redirect while authentication is still loading
    if (authLoading) {
      console.log('GuestList: Auth still loading...');
      return;
    }
    
    if (!user) {
      console.log('GuestList: No user found, redirecting to auth...');
      navigate('/auth');
      return;
    }
    
    console.log('GuestList: User authenticated, loading contacts...');
    loadContacts();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    filterContacts();
  }, [contacts, searchQuery, selectedTags]);

  const loadContacts = async () => {
    try {
      console.log('GuestList: Starting to load contacts...');
      setLoading(true);
      setError(null);
      
      const data = await guestContactService.getUserGuestContacts();
      console.log('GuestList: Loaded contacts successfully:', data);
      
      setContacts(data);
      
      // Extract all unique tags
      const tags = new Set<string>();
      data.forEach(contact => {
        contact.tags?.forEach(tag => tags.add(tag));
      });
      setAllTags(Array.from(tags).sort());
      
    } catch (error) {
      console.error('GuestList: Error loading contacts:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to load contacts: ${errorMessage}`);
      toast.error(`Failed to load contacts: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = contacts;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(contact =>
        contact.name.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query) ||
        contact.phone?.includes(query)
      );
    }

    // Filter by selected tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(contact =>
        selectedTags.some(tag => contact.tags?.includes(tag))
      );
    }

    setFilteredContacts(filtered);
  };

  const handleCreateContact = async (data: CreateGuestContactData) => {
    try {
      await guestContactService.createGuestContact(data);
      toast.success('Contact created successfully');
      loadContacts();
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Failed to create contact');
    }
  };

  const handleUpdateContact = async (id: string, data: CreateGuestContactData) => {
    try {
      await guestContactService.updateGuestContact(id, data);
      toast.success('Contact updated successfully');
      loadContacts();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  };

  const handleDeleteContact = async (id: string) => {
    try {
      await guestContactService.deleteGuestContact(id);
      toast.success('Contact deleted successfully');
      loadContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
  };

  const exportContacts = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Tags'].join(','),
      ...filteredContacts.map(contact =>
        [
          contact.name,
          contact.email || '',
          contact.phone || '',
          contact.tags?.join(';') || ''
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'guest-contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return null;

  if (error) {
    return (
      <>
        <NavigationBar />
        <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 py-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-destructive">Error Loading Guest List</CardTitle>
                <CardDescription>{error}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={loadContacts}>Try Again</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <NavigationBar />
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="container mx-auto px-4 py-4 md:py-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                  <Users className="h-6 w-6 md:h-8 md:w-8 text-primary" />
                  Guest Contacts
                </h1>
                <p className="text-muted-foreground mt-1">
                  Manage your reusable guest contacts for faster event creation
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  onClick={exportContacts} 
                  variant="outline" 
                  disabled={filteredContacts.length === 0}
                  className="w-full sm:w-auto"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <GuestContactDialog
                  onSave={handleCreateContact}
                  trigger={
                    <Button className="w-full sm:w-auto">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Contact
                    </Button>
                  }
                />
              </div>
            </div>
          </div>

          {/* Search and Stats */}
          <Card className="mb-6">
            <CardContent className="p-4 md:p-6">
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search by name, email, or phone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {(searchQuery || selectedTags.length > 0) && (
                    <Button variant="outline" onClick={clearFilters} className="shrink-0">
                      <X className="h-4 w-4 mr-2" />
                      Clear Filters
                    </Button>
                  )}
                </div>

                {/* Stats */}
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>Total: {contacts.length} contacts</span>
                  {searchQuery || selectedTags.length > 0 ? (
                    <span>Filtered: {filteredContacts.length} results</span>
                  ) : null}
                </div>

                {/* Tag Filters - Collapsible on mobile */}
                {allTags.length > 0 && (
                  <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between md:hidden">
                        <span className="flex items-center gap-2">
                          <Filter className="h-4 w-4" />
                          Filter by tags ({selectedTags.length})
                        </span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
                      </Button>
                    </CollapsibleTrigger>
                    
                    <div className="hidden md:block">
                      <div className="flex flex-wrap gap-2 items-center">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
                          <Filter className="h-4 w-4" />
                          Filter by tags:
                        </div>
                        {allTags.map(tag => (
                          <Badge
                            key={tag}
                            variant={selectedTags.includes(tag) ? "default" : "outline"}
                            className="cursor-pointer hover-scale"
                            onClick={() => toggleTag(tag)}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <CollapsibleContent className="md:hidden">
                      <div className="flex flex-wrap gap-2 pt-3">
                        {allTags.map(tag => (
                          <Badge
                            key={tag}
                            variant={selectedTags.includes(tag) ? "default" : "outline"}
                            className="cursor-pointer hover-scale"
                            onClick={() => toggleTag(tag)}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contacts Display */}
          {loading ? (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="animate-fade-in">Loading contacts...</div>
              </CardContent>
            </Card>
          ) : filteredContacts.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="animate-fade-in space-y-4">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground" />
                  <div>
                    <h3 className="text-lg font-medium mb-2">
                      {contacts.length === 0 ? 'No contacts yet' : 'No contacts found'}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {contacts.length === 0 
                        ? 'Create your first contact to get started!'
                        : 'Try adjusting your search or filters.'
                      }
                    </p>
                    {contacts.length === 0 && (
                      <GuestContactDialog
                        onSave={handleCreateContact}
                        trigger={
                          <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Your First Contact
                          </Button>
                        }
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Card>
                  <CardContent className="p-0">
                    <div className="rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead>Tags</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredContacts.map((contact) => (
                            <TableRow key={contact.id} className="hover:bg-muted/50">
                              <TableCell className="font-medium">{contact.name}</TableCell>
                              <TableCell>{contact.email || '-'}</TableCell>
                              <TableCell>{contact.phone || '-'}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {contact.tags?.map(tag => (
                                    <Badge key={tag} variant="secondary" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <GuestContactDialog
                                    contact={contact}
                                    onSave={(data) => handleUpdateContact(contact.id, data)}
                                    trigger={
                                      <Button variant="ghost" size="sm">
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                    }
                                  />
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="sm">
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to delete {contact.name}? This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => handleDeleteContact(contact.id)}>
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filteredContacts.map((contact) => (
                  <Card key={contact.id} className="overflow-hidden hover-scale">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-lg truncate">{contact.name}</h3>
                          
                          <div className="space-y-2 mt-2">
                            {contact.email && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Mail className="h-4 w-4 shrink-0" />
                                <span className="truncate">{contact.email}</span>
                              </div>
                            )}
                            
                            {contact.phone && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="h-4 w-4 shrink-0" />
                                <span>{contact.phone}</span>
                              </div>
                            )}
                            
                            {contact.tags && contact.tags.length > 0 && (
                              <div className="flex items-start gap-2">
                                <Tag className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                                <div className="flex flex-wrap gap-1">
                                  {contact.tags.map(tag => (
                                    <Badge key={tag} variant="secondary" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="shrink-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <GuestContactDialog
                              contact={contact}
                              onSave={(data) => handleUpdateContact(contact.id, data)}
                              trigger={
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                  <Edit2 className="h-4 w-4 mr-2" />
                                  Edit Contact
                                </DropdownMenuItem>
                              }
                            />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Contact
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete {contact.name}? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteContact(contact.id)}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default GuestList;